import {
  type EditToolBoxShadowValues,
  type EditToolButtonAction,
  type EditToolColorAndShape,
  type EditToolHighlightElementOptionsValues,
  type EditToolLayoutValues,
  type EditToolNavigationOptionsValues,
  type EditToolSpotlightOptionsValues,
  type EditToolTextStyle,
  RelativePositionAlignmentEnums,
  RelativePositionTypeEnums,
} from './EditTool';

export interface TourStepTooltipBalloonConfig {
  colorAndShape: EditToolColorAndShape;
  boxShadow: EditToolBoxShadowValues;
  layout: EditToolLayoutValues;
  layoutBeforeConverting?: EditToolLayoutValues;
  showArrow: boolean;
}

export interface TourStepTooltipButtonConfig {
  colorAndShape: EditToolColorAndShape;
  text: EditToolTextStyle;
  action?: EditToolButtonAction;
  label?: string;
}
export interface TourStepTooltipButtonsConfig {
  nextButton: TourStepTooltipButtonConfig;
  previousButton: TourStepTooltipButtonConfig;
  lastStepButton: TourStepTooltipButtonConfig;
}

export interface TourStepTooltipTextConfig {
  title: EditToolTextStyle;
  content: EditToolTextStyle;
  stepCount?: EditToolTextStyle;
}

export interface TourStepTooltipConfig {
  balloon: TourStepTooltipBalloonConfig;
  buttons: TourStepTooltipButtonsConfig;
  text: TourStepTooltipTextConfig;
  navigationOptions: EditToolNavigationOptionsValues;
  highlightElementOptions: EditToolHighlightElementOptionsValues;
  spotlightOptions: EditToolSpotlightOptionsValues;
}

export interface TourStepConfig {
  tooltip: TourStepTooltipConfig;
}

export interface TourStepConfigUpdate {
  path: string;
  value: any;
}

export interface TourStepConfigUpdateRequest {
  stepId: string;
  prevTooltipConfig: TourStepTooltipConfig;
  newTooltipConfig: TourStepTooltipConfig;
}

export interface TourStepConfigUpdateData {
  stepId: string | null;
  updates: TourStepConfigUpdate[];
}

export const tooltipDefaultConfig: TourStepTooltipConfig = {
  balloon: {
    colorAndShape: {
      backgroundColor: {
        r: 55,
        g: 54,
        b: 63,
        a: 1,
      },
      borderRadius: {
        leftUp: 12,
        rightUp: 12,
        leftDown: 12,
        rightDown: 12,
      },
      padding: {
        top: 10,
        right: 10,
        bottom: 10,
        left: 10,
      },
    },
    boxShadow: {
      isActive: true,
      boxShadowColor: {
        r: 0,
        g: 0,
        b: 0,
        a: 0.1,
      },
      boxShadowParams: {
        blur: 30,
        spread: 10,
        x: 10,
        y: 10,
      },
    },
    layout: {
      relation: RelativePositionTypeEnums.page,
      alignment: RelativePositionAlignmentEnums.middleCenter,
    },
    showArrow: true,
  },
  buttons: {
    nextButton: {
      colorAndShape: {
        backgroundColor: {
          r: 244,
          g: 243,
          b: 237,
          a: 1,
        },
        borderRadius: {
          leftUp: 6,
          rightUp: 6,
          leftDown: 6,
          rightDown: 6,
        },
        padding: {
          top: 5,
          right: 16,
          bottom: 5,
          left: 16,
        },
      },
      text: {
        fontType: {
          fontFamily: 'Inter, sans-serif',
          fontSize: 14,
        },
        textColor: { r: 55, g: 54, b: 63, a: 1 },
        textFormat: {
          bold: false,
          italic: false,
          underline: false,
          alignText: 'textLeft',
        },
      },
      action: {
        name: 'nextStep',
        url: '',
      },
      label: 'Next',
    },
    previousButton: {
      colorAndShape: {
        backgroundColor: {
          r: 55,
          g: 54,
          b: 63,
          a: 1,
        },
        borderRadius: {
          leftUp: 6,
          rightUp: 6,
          leftDown: 6,
          rightDown: 6,
        },
        padding: {
          top: 5,
          right: 16,
          bottom: 5,
          left: 16,
        },
      },
      text: {
        fontType: {
          fontFamily: 'Inter, sans-serif',
          fontSize: 14,
        },
        textColor: { r: 244, g: 243, b: 237, a: 1 },
        textFormat: {
          bold: false,
          italic: false,
          underline: false,
          alignText: 'textLeft',
        },
      },
      action: {
        name: 'previousStep',
        url: '',
      },
      label: 'Back',
    },
    lastStepButton: {
      colorAndShape: {
        backgroundColor: {
          r: 244,
          g: 243,
          b: 237,
          a: 1,
        },
        borderRadius: {
          leftUp: 6,
          rightUp: 6,
          leftDown: 6,
          rightDown: 6,
        },
        padding: {
          top: 5,
          right: 16,
          bottom: 5,
          left: 16,
        },
      },
      text: {
        fontType: {
          fontFamily: 'Inter, sans-serif',
          fontSize: 14,
        },
        textColor: { r: 55, g: 54, b: 63, a: 1 },
        textFormat: {
          bold: false,
          italic: false,
          underline: false,
          alignText: 'textLeft',
        },
      },
      action: {
        name: 'startOverTour',
        url: '',
      },
      label: 'Restart tour',
    },
  },
  text: {
    title: {
      fontType: {
        fontFamily: 'Inter, sans-serif',
        fontSize: 14,
      },
      textColor: { r: 244, g: 243, b: 237, a: 1 },
      textFormat: {
        bold: false,
        italic: false,
        underline: false,
        alignText: 'textLeft',
      },
    },
    content: {
      fontType: {
        fontFamily: 'Inter, sans-serif',
        fontSize: 12,
      },
      textColor: { r: 244, g: 243, b: 237, a: 1 },
      textFormat: {
        bold: false,
        italic: false,
        underline: false,
        alignText: 'textLeft',
      },
    },
    stepCount: {
      fontType: {
        fontFamily: 'Inter, sans-serif',
        fontSize: 12,
      },
      textColor: { r: 244, g: 243, b: 237, a: 1 },
      textFormat: {
        bold: false,
        italic: false,
        underline: false,
        alignText: 'textLeft',
      },
    },
  },
  navigationOptions: {
    isButtonsNavigation: true,
    isElementNavigation: false,
    elementAction: {
      name: 'nextStep',
      url: '',
      goToStepId: null,
    },
  },
  highlightElementOptions: {
    isActive: false,
    color: { r: 113, g: 229, b: 255, a: 1 },
  },
  spotlightOptions: {
    isActive: false,
    color: { r: 55, g: 54, b: 63, a: 0.5 },
  },
};
